import { queryClient } from "./reactQuery/config.js";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { LoginAuthProvider } from "./context/LoginAuthContext.js";
import { QuestionsProvider } from "./context/QuestionsContext.js";
import { DoingPlayerProvider } from "./context/DoingPlayerContext.js";
import { FinalFeedbackProvider } from "./context/FinalFeedbackContext.js";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ColorModeProvider } from "./context/ColorModeContext";
import GlobalStylesMUI from "./theme/GlobalStylesMUI";
import ProtectedRoute from "./ui/ProtectedRoute";
import RequireUsername from "./features/auth/RequireUsername";
import RequireHasInfo from "./features/questions/RequireHasInfo";
import SpinnerFullPage from "./ui/SpinnerFullPage";
import PageContainer from "./ui/PageContainer";
import PwaInstallPrompt from "./ui/PwaInstallPrompt";
import ErrorModal from "./ui/ErrorModal";
import ToasterConfig from "./ui/ToasterConfig";
import AdtraceBootstrap from "./features/analytics/AdtraceBootstrap.js";
import AdtraceGate from "./features/analytics/AdtraceGate.js";

//LAUNNCH SCREEN
const LaunchScreen = lazy(() => import("./pages/LaunchScreen"));
const SplashVideo = lazy(() => import("./pages/SplashVideo"));

//QUESTIONS
const Questions = lazy(() => import("./pages/Questions"));

//AUTH
const Login = lazy(() => import("./pages/Login"));
const LoginEntry = lazy(() => import("./features/auth/LoginEntry"));
const VerifyOtp = lazy(() => import("./features/auth/VerifyOtp"));
const AuthSuccess = lazy(() => import("./features/auth/AuthSuccess"));
const UserGate = lazy(() => import("./features/auth/UserGate"));
const SetUserName = lazy(() => import("./features/auth/SetUserName"));
const Welcome = lazy(() => import("./pages/Welcome"));

//REPORTS
const Reports = lazy(() => import("./pages/Reports"));
const PracticalReports = lazy(() => import("./pages/PracticalReports"));
const AthleteReportAfterQuestions = lazy(
  () => import("./pages/AthleteReportAfterQuestions")
);
const AthletePhysicalReport = lazy(
  () => import("./pages/AthletePhysicalReport")
);

//DASHBOARD
const AppLayout = lazy(() => import("./ui/AppLayout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

//WEEKLY PLAN
const Weekly = lazy(() => import("./pages/Weekly"));
const WeeklyPlan = lazy(() => import("./features/weeklyPlan/WeeklyPlan"));
const CalendarDetails = lazy(() => import("./pages/CalendarDetails"));
const Trainers = lazy(() => import("./pages/Trainers"));
const MoveDetails = lazy(() => import("./pages/MoveDetails"));
const ReplaceMove = lazy(() => import("./pages/ReplaceMove"));
const DoingPlayer = lazy(() => import("./pages/DoingPlayer"));
const CompleteTraining = lazy(() => import("./pages/CompleteTraining"));
const FinalFeedbackForm = lazy(() => import("./pages/FinalFeedbackForm"));
const WeeklySubscription = lazy(() => import("./pages/WeeklySubscription"));

//PACKAGES
const PackageLayout = lazy(() => import("./features/packages/PackagesLayout"));
const Packages = lazy(() => import("./pages/Packages"));
const PackageDetails = lazy(() => import("./pages/PackageDetails"));
const PurchaseSuccess = lazy(() => import("./pages/PurchaseSuccess"));
const PurchaseError = lazy(() => import("./pages/PurchaseError"));

//TRAINERS
const TrainersLayout = lazy(() => import("./features/trainers/TrainersLayout"));
const TfitTrainers = lazy(() => import("./pages/TfitTrainers"));
const Trainer = lazy(() => import("./pages/Trainer"));

//PROFILE
const ProfileLayout = lazy(() => import("./features/profile/ProfileLayout"));
const Profile = lazy(() => import("./pages/Profile"));
const Account = lazy(() => import("./pages/Account"));
const ProfileQuestions = lazy(() => import("./pages/ProfileQuestions"));
const ReviewWeeklyInfo = lazy(() => import("./pages/ReviewWeeklyInfo"));
const ProfileQuestionDetails = lazy(
  () => import("./pages/ProfileQuestionDetails")
);
const ProfileVerifyOtp = lazy(() => import("./pages/ProfileVerifyOtp"));
const MyPackages = lazy(() => import("./pages/MyPackages"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));

const PageNotFound = lazy(() => import("./pages/PageNotFound"));

function App() {
  return (
    <ColorModeProvider>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} aria-hidden={false} />
        <GlobalStylesMUI />
        <LoginAuthProvider>
          <ToasterConfig />
          <PageContainer>
            <BrowserRouter>
              <AdtraceBootstrap />
              <Suspense fallback={<SpinnerFullPage />}>
                <PwaInstallPrompt />
                <Routes>
                  {/* Onboarding Routes */}
                  <Route
                    index
                    element={<Navigate replace to="launch-screen" />}
                  />
                  <Route path="launch-screen" element={<LaunchScreen />} />
                  <Route path="splash-video" element={<SplashVideo />} />

                  {/* Auth Routes */}
                  <Route path="login" element={<Login />}>
                    <Route index element={<LoginEntry />} />
                    <Route path="verify-otp" element={<VerifyOtp />} />
                    <Route path="google-auth" element={<AuthSuccess />} />
                    <Route path="user-gate" element={<UserGate />} />
                    <Route
                      path="username"
                      element={
                        <ProtectedRoute>
                          <SetUserName />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="welcome"
                    element={
                      <ProtectedRoute>
                        <RequireUsername>
                          <Welcome />
                        </RequireUsername>
                      </ProtectedRoute>
                    }
                  />

                  {/* Question Routes */}
                  <Route
                    path="questions"
                    element={
                      <ProtectedRoute>
                        <RequireUsername>
                          <QuestionsProvider>
                            <Questions />
                          </QuestionsProvider>
                        </RequireUsername>
                      </ProtectedRoute>
                    }
                  />

                  {/* Main App Routes */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <AdtraceGate />
                        <RequireUsername>
                          <RequireHasInfo>
                            <AppLayout />
                          </RequireHasInfo>
                        </RequireUsername>
                      </ProtectedRoute>
                    }
                  >
                    {/* Dashboard Route*/}
                    <Route
                      index
                      element={<Navigate replace to="dashboard" />}
                    />
                    <Route path="dashboard" element={<Dashboard />} />

                    {/* Report Routes */}
                    <Route path="reports" element={<Reports />}>
                      <Route
                        index
                        element={
                          <Navigate replace to="practical-report?scope=7" />
                        }
                      />
                      <Route
                        path="practical-report"
                        element={<PracticalReports />}
                      />
                      <Route
                        path="questions-report"
                        element={<AthleteReportAfterQuestions />}
                      />
                      <Route
                        path="physical-report"
                        element={<AthletePhysicalReport />}
                      />
                    </Route>

                    {/* Weekly Plan Routes */}
                    <Route
                      path="weekly"
                      element={
                        <DoingPlayerProvider>
                          <WeeklyPlan />
                        </DoingPlayerProvider>
                      }
                    >
                      <Route index element={<Weekly />} />
                      <Route path="calendar" element={<CalendarDetails />} />
                      <Route path="trainers" element={<Trainers />} />
                      <Route
                        path="move-datails/:moveId"
                        element={<MoveDetails />}
                      />
                      <Route
                        path="replace-move/:moveId"
                        element={<ReplaceMove />}
                      />
                      <Route path="doing" element={<DoingPlayer />} />
                      <Route path="complete" element={<CompleteTraining />} />
                      <Route
                        path="final-feedback"
                        element={
                          <FinalFeedbackProvider>
                            <FinalFeedbackForm />
                          </FinalFeedbackProvider>
                        }
                      />
                      <Route
                        path="subscription"
                        element={<WeeklySubscription />}
                      />
                      <Route
                        path="purchase/success"
                        element={<PurchaseSuccess />}
                      />
                      <Route
                        path="purchase/error"
                        element={<PurchaseError />}
                      />
                    </Route>

                    {/* Packages Routes */}
                    <Route path="packages" element={<PackageLayout />}>
                      <Route index element={<Packages />} />
                      <Route
                        path="package/:packageId"
                        element={<PackageDetails />}
                      />
                      <Route
                        path="purchase/success/:packageId"
                        element={<PurchaseSuccess />}
                      />
                      <Route
                        path="purchase/error/:packageId"
                        element={<PurchaseError />}
                      />
                    </Route>

                    {/* Trainers Routes */}
                    <Route path="trainers" element={<TrainersLayout />}>
                      <Route index element={<TfitTrainers />} />
                      <Route path="trainer/:trainerId" element={<Trainer />} />
                    </Route>

                    {/* Profile Routes */}
                    <Route
                      path="profile"
                      element={
                        <QuestionsProvider>
                          <ProfileLayout />
                        </QuestionsProvider>
                      }
                    >
                      <Route index element={<Profile />} />
                      <Route path="account" element={<Account />} />
                      <Route path="sport-info" element={<ProfileQuestions />} />
                      <Route
                        path="question/:questionId"
                        element={<ProfileQuestionDetails />}
                      />
                      <Route
                        path="review-weekly-info"
                        element={<ReviewWeeklyInfo />}
                      />
                      <Route
                        path="review/:questionId"
                        element={<ProfileQuestionDetails />}
                      />

                      <Route path="verify-otp" element={<ProfileVerifyOtp />} />
                      <Route path="packages" element={<MyPackages />} />
                      <Route
                        path="payment-history"
                        element={<PaymentHistory />}
                      />
                    </Route>
                  </Route>

                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <ErrorModal />
          </PageContainer>
        </LoginAuthProvider>
      </QueryClientProvider>
    </ColorModeProvider>
  );
}

export default App;
